import { B2CTheme, FKTheme } from '@cleartrip/ct-design-theme'

const { color } = B2CTheme

/**
 * Shared tuple palette + expandable-group themes.
 * Prefer B2C/FK tokens; hex/rgba only when no token exists.
 */
export const TUPLE_COLORS = {
  heading: color.text.heading,
  white: color.background.neutral,
  brand: color.background.brand,
  link: color.text.link,
  linkLightBg: color.background.linkLightBg,
  success: color.text.success,
  successBg: color.background.successLightBg,
  orange100: color.badge.orange100,
  alert500: color.text.alert500,
  warningLine: FKTheme.color.button.containedSecondaryBg,
  borderDisabled: color.border.disabled,
  borderDisabledDark: color.border.disabledDark,
  /** Primary heading @ 10% — no border token. */
  cardBorder: 'rgba(26,26,26,0.10)',
  metaDivider: 'rgba(26,26,26,0.12)',
  /** Trip-blue link @ 25% — expanded same-price shell. */
  linkSoftBorder: 'rgba(15,107,255,0.25)',
  /** Figma same-price shell — no B2C token (closest is linkLightBg). */
  samePriceShell: '#E5EBFA',
  /** High-RPS shell — badge.orange100 (#FFF1EC ≈ Figma #FFF1ED). */
  highRpsShell: color.badge.orange100,
  selectedShell: '#0A4097',
  unselectedShell: '#DDEAFF',
  linkOnDark: '#76bfff',
  masterCalloutGreen: '#1FDB7A',
  masterCalloutShimmer: 'rgba(31,219,122,0.15)',
  masterBarBg: 'rgba(26,26,26,0.75)',
  masterBarShadow: '0px 0px 16px 0px rgba(26,26,26,0.16)',
  white75: 'rgba(255,255,255,0.75)',
  dividerOnDark: 'rgba(255,255,255,0.2)',
  tooltipBg: color.background.defaultDarkest,
  tooltipText: color.background.neutral,
  tooltipHighlight: '#FFBA3B',
} as const

export const TUPLE_TOOLTIP = {
  padding: 12,
  radius: B2CTheme.border.radius[8],
  gap: 12,
  arrowWidth: 20,
  arrowHeight: 8,
  zIndex: B2CTheme.zIndex.tooltip,
} as const

export const TUPLE_PRICE_ACTION_GAP = 20

export const TUPLE_HOVER_SHADOW = '0 4px 16px 0 rgba(26, 26, 26, 0.08)'

// Underline for hoverable route/stops text (nearby airport, stops).
export const TUPLE_HOVER_UNDERLINE_CSS = { width: '100%', borderTop: '2px dotted #B3B3B3' }

export interface TupleGroupPadding {
  top: number
  right: number
  bottom: number
  left: number
}

export interface TupleGroupTheme {
  shellBg: string
  borderColor: string
  expandedBorderColor: string
  expandedShadow: string
  collapsedPadding: TupleGroupPadding
  expandedPadding: TupleGroupPadding
}

export const SAME_PRICE_THEME: TupleGroupTheme = {
  shellBg: TUPLE_COLORS.samePriceShell,
  borderColor: TUPLE_COLORS.cardBorder,
  expandedBorderColor: TUPLE_COLORS.linkSoftBorder,
  expandedShadow: 'none',
  collapsedPadding: { top: 4, right: 4, bottom: 0, left: 4 },
  expandedPadding: { top: 12, right: 12, bottom: 8, left: 12 },
}

export const SAME_PRICE_CARD_RADIUS = 12

export const HIGH_RPS_THEME: TupleGroupTheme = {
  shellBg: TUPLE_COLORS.highRpsShell,
  borderColor: TUPLE_COLORS.cardBorder,
  expandedBorderColor: TUPLE_COLORS.cardBorder,
  expandedShadow: 'none',
  collapsedPadding: { top: 12, right: 12, bottom: 12, left: 12 },
  expandedPadding: { top: 12, right: 12, bottom: 12, left: 12 },
}
